import { Plus } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { DirectMessageModal } from '@/modules/messages/components/DirectMessageModal'
import { Button } from '@/shared/components/ui/button'
import { ConfirmDialog } from '@/shared/components/ui/ConfirmDialog'
import { PlaceholderPage } from '@/shared/components/ui/PlaceholderPage'

import { AddSalesDialog } from '../components/sales/AddSalesDialog'
import { AssignTargetDialog } from '../components/sales/AssignTargetDialog'
import { MonthlyTargetsModal } from '../components/sales/MonthlyTargetsModal'
import { useSales } from '../hooks/useSales'

import AdminSalesPage from './AdminSalesPage'

export default function AdminSalesRoute() {
  const [open, setOpen] = useState(false)
  const [selectedSales, setSelectedSales] = useState(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [salesToDelete, setSalesToDelete] = useState(null)

  // Target Assignment State
  const [targetDialogOpen, setTargetDialogOpen] = useState(false)
  const [targetSales, setTargetSales] = useState(null)

  // Target History & Achievement State
  const [viewTargetsDialogOpen, setViewTargetsDialogOpen] = useState(false)
  const [viewTargetsSales, setViewTargetsSales] = useState(null)

  // Direct Messaging State
  const [messageModalOpen, setMessageModalOpen] = useState(false)
  const [messagePartner, setMessagePartner] = useState(null)

  const { deleteSalesPerson, assignSalesTarget } = useSales()

  const handleAdd = () => {
    setSelectedSales(null)
    setOpen(true)
  }

  const handleEdit = (sales) => {
    setSelectedSales(sales)
    setOpen(true)
  }

  const handleDelete = (sales) => {
    setSalesToDelete(sales)
    setConfirmOpen(true)
  }

  const handleMessage = (sales) => {
    setMessagePartner(sales)
    setMessageModalOpen(true)
  }

  const handleAssignTarget = (sales) => {
    setTargetSales(sales)
    setTargetDialogOpen(true)
  }

  const handleViewTargets = (sales) => {
    setViewTargetsSales(sales)
    setViewTargetsDialogOpen(true)
  }

  const confirmDelete = async () => {
    if (!salesToDelete) return

    try {
      await deleteSalesPerson(salesToDelete._id)

      toast.success('Sales person deleted successfully.')

      setConfirmOpen(false)
      setSalesToDelete(null)
    } catch (error) {
      toast.error('Failed to delete sales person.')
      console.error(error)
    }
  }

  return (
    <div className="space-y-6">
      <PlaceholderPage
        title="Sales"
        description="Manage sales persons."
        action={
          <Button
            size="lg"
            className="bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-2xl"
            onClick={handleAdd}
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Sales
          </Button>
        }
      />

      <AdminSalesPage
        onEdit={handleEdit}
        onDelete={handleDelete}
        onMessage={handleMessage}
        onAssignTarget={handleAssignTarget}
        onViewTargets={handleViewTargets}
      />

      <AddSalesDialog
        open={open}
        onOpenChange={setOpen}
        sales={selectedSales}
      />
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Delete Sales Person"
        description={`Are you sure you want to delete "${salesToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDelete}
      />

      <AssignTargetDialog
        open={targetDialogOpen}
        onOpenChange={setTargetDialogOpen}
        sales={targetSales}
        onAssignTarget={assignSalesTarget}
      />

      <MonthlyTargetsModal
        open={viewTargetsDialogOpen}
        onOpenChange={setViewTargetsDialogOpen}
        sales={viewTargetsSales}
      />

      <DirectMessageModal
        open={messageModalOpen}
        onOpenChange={setMessageModalOpen}
        partner={messagePartner}
      />
    </div>
  )
}
